import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileSignature, FileCheck2, Eye, FileText, Loader2 } from "lucide-react";

const STATUS_STYLE = {
  pending_assignor_signature: "bg-amber-50 border-amber-200 text-amber-700",
  pending_assignee_signature: "bg-blue-50 border-blue-200 text-blue-700",
  fully_executed: "bg-emerald-50 border-emerald-200 text-emerald-700",
};

function partyOf(user, a) {
  if (user?.user_id && a.assignee_user_id === user.user_id) return "assignee";
  if (user?.user_id && a.assignor_user_id === user.user_id) return "assignor";
  if (user?.role === "admin" && a.assignor_kind === "platform") return "assignor";
  return null;
}

function isMyTurn(user, a) {
  const me = partyOf(user, a);
  if (!me) return false;
  if (a.status === "pending_assignor_signature") return me === "assignor";
  if (a.status === "pending_assignee_signature") return me === "assignee";
  return false;
}

function statusLabel(user, a) {
  if (a.status === "fully_executed") return "Fully Executed";
  if (a.status === "pending_assignor_signature") {
    return partyOf(user, a) === "assignor" ? "Awaiting Your Signature" : "Awaiting Seller Signature";
  }
  if (a.status === "pending_assignee_signature") {
    return partyOf(user, a) === "assignee" ? "Awaiting Your Signature" : "Awaiting Buyer Signature";
  }
  return a.status;
}

export default function AssignmentManager({ scope = "mine" }) {
  // scope: "mine" — investor/dealer view of their assignments
  //        "admin" — admin view (all)
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [signFor, setSignFor] = useState(null);
  const [fullName, setFullName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const apiBase = (api.defaults?.baseURL || "").replace(/\/+$/, "");

  const load = async () => {
    try {
      const r = await api.get("/assignments");
      setItems(r.data || []);
    } catch (e) {
      toast.error("Failed to load assignments");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const submitSign = async () => {
    if (!fullName.trim()) { toast.error("Type your full name"); return; }
    if (!agree) { toast.error("You must confirm the assignment terms"); return; }
    setBusy(true);
    try {
      await api.post(`/assignments/${signFor.assignment_id}/sign`,
        { full_name: fullName.trim(), agree: true });
      toast.success("Signed");
      setSignFor(null); setFullName(""); setAgree(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const openPdf = async (a) => {
    try {
      const r = await api.get(`/assignments/${a.assignment_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPdfUrl({ url, id: a.assignment_id });
    } catch {
      toast.error("Failed to load PDF");
    }
  };

  if (items === null) {
    return <div className="border border-border p-8 flex items-center gap-3 text-muted-foreground" data-testid="assignments-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="assignment-manager">
      <div className="flex items-center gap-3">
        <FileSignature className="w-5 h-5 text-primary" />
        <div className="font-overline text-accent">Note Assignments</div>
      </div>
      <p className="text-xs text-muted-foreground">
        Formal assignment documents are generated automatically once escrow is released. Both parties must e-sign for the transfer to complete.
      </p>

      {items.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="assignments-empty">
          No assignments yet. They appear here after the admin releases escrow on your purchases.
        </div>
      ) : items.map((a) => (
        <div key={a.assignment_id} className="border border-border p-4" data-testid={`assignment-row-${a.assignment_id}`}>
          <div className="flex flex-wrap justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-data text-sm">{a.assignment_id}</span>
                <span className={`px-2 py-0.5 border text-xs font-overline ${STATUS_STYLE[a.status] || ""}`}
                  data-testid={`assignment-status-${a.assignment_id}`}>
                  {statusLabel(user, a)}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Note <code>{a.note_id}</code> · Assignor <b>{a.assignor_name}</b> ({a.assignor_kind}) · Price ${Number(a.purchase_price || 0).toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex gap-3 flex-wrap">
                <SigStatus party="Assignor" sig={a.signatures?.assignor} />
                <SigStatus party="Assignee" sig={a.signatures?.assignee} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-none"
                onClick={() => openPdf(a)} data-testid={`assignment-view-${a.assignment_id}`}>
                <Eye className="w-4 h-4 mr-1" /> View PDF
              </Button>
              {isMyTurn(user, a) && (
                <Button size="sm" className="rounded-none"
                  onClick={() => { setSignFor(a); setFullName(""); setAgree(false); }}
                  data-testid={`assignment-sign-${a.assignment_id}`}>
                  <FileCheck2 className="w-4 h-4 mr-1" /> Sign
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}

      {signFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="assignment-sign-modal" onClick={(e) => e.target === e.currentTarget && setSignFor(null)}>
          <div className="bg-background border border-border max-w-lg w-full p-6 space-y-4">
            <div className="font-overline text-accent">Electronic Signature</div>
            <h3 className="font-display text-xl">Sign assignment {signFor.assignment_id}</h3>
            <div className="text-xs text-muted-foreground space-y-2 max-h-40 overflow-y-auto p-3 bg-muted/50">
              <p>By typing your name and clicking Sign, you agree:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>You are the party identified in this Assignment.</li>
                <li>You intend your typed name to be your legal electronic signature.</li>
                <li>You consent to electronic delivery and execution under E-SIGN/UETA.</li>
                <li>You have reviewed the Assignment PDF and agree to its terms.</li>
              </ul>
            </div>
            <div>
              <Label className="font-overline">Type your full legal name</Label>
              <Input className="rounded-none mt-2 font-mono-data" value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="First Last" data-testid="assignment-sign-name" />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="assignment-sign-agree" />
              <span>I consent to electronic signature and have reviewed the Assignment.</span>
            </label>
            <div className="flex gap-2">
              <Button onClick={submitSign} disabled={busy || !fullName.trim() || !agree}
                className="flex-1 rounded-none uppercase tracking-wider h-11" data-testid="assignment-sign-submit">
                {busy ? "Signing…" : "Sign Electronically"}
              </Button>
              <Button onClick={() => setSignFor(null)} variant="outline" className="rounded-none uppercase tracking-wider h-11">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {pdfUrl && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="assignment-pdf-modal"
          onClick={(e) => { if (e.target === e.currentTarget) { URL.revokeObjectURL(pdfUrl.url); setPdfUrl(null); }}}>
          <div className="bg-background border border-border max-w-4xl w-full h-[90vh] flex flex-col">
            <div className="p-3 flex items-center justify-between border-b border-border">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                <span className="font-mono-data text-sm">{pdfUrl.id}</span>
              </div>
              <Button size="sm" variant="outline" className="rounded-none"
                onClick={() => { URL.revokeObjectURL(pdfUrl.url); setPdfUrl(null); }}
                data-testid="assignment-pdf-close">Close</Button>
            </div>
            <iframe src={pdfUrl.url} title="Assignment PDF" className="flex-1 w-full bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}

function SigStatus({ party, sig }) {
  if (sig?.name) {
    return (
      <span className="inline-flex items-center gap-1">
        <FileCheck2 className="w-3 h-3 text-emerald-600" />
        <b>{party}:</b> <span className="font-mono-data">{sig.name}</span>
      </span>
    );
  }
  return <span className="text-muted-foreground"><b>{party}:</b> pending</span>;
}
